import { Plus, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSidebarStore } from "@/atoms/playground";
import { isAvailableHistoryTrigger, useHistorySidebarAccessibility } from "./history-sidebar-accessibility";
import { usePlaygroundPresentation } from "./playground-presentation";

export function PlaygroundHistoryPanel({ children, search, onSearchChange, onNewChat }: {
  children: ReactNode; search: string; onSearchChange: (value: string) => void; onNewChat: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { mobile, mobileHistoryOpen, setMobileHistoryOpen, mobileFocusAllowed, settingsOpen } = usePlaygroundPresentation();
  const sidebar = useSidebarStore();
  const accessibility = useHistorySidebarAccessibility();
  const open = mobile ? mobileHistoryOpen : sidebar.isOpen && !settingsOpen;
  const close = () => mobile ? setMobileHistoryOpen(false) : sidebar.toggle();
  const heading = mobile
    ? <SheetTitle className="text-sm font-medium leading-5">{t("conversation.conversations")}</SheetTitle>
    : <h2 className="text-sm font-medium leading-5">{t("conversation.conversations")}</h2>;
  const content = <div ref={accessibility?.panel} className="flex min-h-0 flex-1 flex-col">
    <div className="flex min-h-14 shrink-0 items-center justify-between gap-2 px-4 py-3">
      {heading}
      <Button ref={accessibility?.closer} type="button" variant="ghost" size="icon"
        className="size-6 rounded-sm motion-reduce:transition-none" aria-label={t("playground.closeSidebar")}
        aria-expanded={open} aria-controls={accessibility?.id} onClick={close}>
        <X aria-hidden="true" className="size-[13.25px]" />
      </Button>
    </div>
    <div className="flex shrink-0 gap-2 px-4 pb-4 pt-3">
      <div className="relative min-w-0 flex-1">
        <Search aria-hidden="true" className="pointer-events-none absolute start-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
        <Input type="search" value={search} onChange={event => onSearchChange(event.target.value)}
          aria-label={t("playground.searchPlaceholder")} placeholder={t("playground.searchPlaceholder")}
          className="h-9 rounded-lg border-border bg-input ps-10 shadow-xs placeholder:text-foreground/70 motion-reduce:transition-none" />
      </div>
      <Button type="button" variant="ghost" size="icon" className="rounded-lg motion-reduce:transition-none"
        aria-label={t("playground.startNewChat")} onClick={() => { onNewChat(); if (mobile) setMobileHistoryOpen(false); }}>
        <Plus aria-hidden="true" className="size-4" />
      </Button>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
  </div>;

  if (!mobile) return <aside id={accessibility?.id} aria-label={t("conversation.conversations")}
    aria-hidden={!open} hidden={!open} {...(!open ? { inert: "" } : {})}
    className={open ? "flex min-h-0 w-[300px] shrink-0 flex-col border-s border-border bg-background" : "hidden"}
    data-playground-history="desktop">{open && content}</aside>;

  return <SheetContent side={i18n.dir() === "rtl" ? "left" : "right"} dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
    id={accessibility?.id} aria-describedby={undefined} showCloseButton={false}
    data-playground-history="mobile" overlayClassName="top-[60px] motion-reduce:animate-none"
    className="top-[60px] h-[calc(100dvh-60px)] w-full max-w-full gap-0 bg-background p-0 motion-reduce:animate-none motion-reduce:transition-none"
    onCloseAutoFocus={event => {
      const trigger = accessibility?.opener.current ?? null;
      const focused = document.activeElement;
      const ownsFocus = focused === document.body || focused === trigger ||
        (event.target instanceof HTMLElement && focused && event.target.contains(focused));
      if (!mobileFocusAllowed?.current || !ownsFocus || !isAvailableHistoryTrigger(trigger)) event.preventDefault();
    }}>{content}</SheetContent>;
}
